import React from 'react';
import parse, { DOMNode, Element } from 'html-react-parser';
import { decode } from 'entities';

import PostCustomImage from '@/components/(blog)/post/PostCustomImage';
import type { TutorialStep } from '@/components/(postTypes)/platforms/(single)/layout/ExchangeTutorial';
import PostNotice from '@/components/(blog)/post/PostNotice';
import PostForecasts from '@/components/(blog)/post/PostForecasts';
import CustomTable from '@/components/(blog)/post/PostCustomTable';
import TradingViewWidget from '@/utils/post/TradingViewWidget';
import Script from 'next/script';

import { ExchangeTutorial, VideoPlayer } from '@/components/(blog)/post/util/LazyEmbeds';

interface Tutorial {
    title: string;
    tutorial: TutorialStep[];
}

interface VideoScript {
    aparat?: string;
    youtube?: string;
}

type ParserReplaceReturn = string | boolean | void | object | Element | null;

/**
 * parseContent
 *
 * Given an HTML string (`processedContent`) and an optional `title` (used for alt-tags),
 * returns a React node tree with all the custom replacements applied.
 *
 * Internally, it checks `isClient` via `typeof window`.
 */
export function parseContent(processedContent: string, title: string = ''): React.ReactNode {
    const isClient = typeof window !== 'undefined';
    const transformNode = (node: DOMNode, index: number): ParserReplaceReturn => {
        if (node.type === 'tag') {
            const element = node as Element;

            // —————————————
            // 1) TUTORIAL SLIDES
            // —————————————
            if (element.attribs?.id === 'tutorial-slides') {
                const tId = parseInt(element.attribs['data-id'], 10);
                const decodedJson = decode(element.attribs['data-tutorial']);
                const tutorials: Tutorial[] = JSON.parse(decodedJson);
                const tutorial = tutorials[tId - 1];

                if (tutorial) {
                    return (
                        <ExchangeTutorial
                            key={`${index}-brokerTutorial`}
                            title={tutorial.title}
                            tutorial={tutorial.tutorial}
                        />
                    );
                }
            }

            // —————————————
            // 2) FORECASTS
            // —————————————
            if (element.attribs?.id === 'forecasts') {
                const decodedJson = decode(element.attribs['data-forecasts']);
                const forecasts = JSON.parse(decodedJson);
                return <PostForecasts key={index} forecasts={forecasts} />;
            }

            // —————————————
            // 3) POST NOTICE
            // —————————————
            if (element.attribs?.id === 'post-notice') {
                const decodedJson = decode(element.attribs['data-attr']);
                const notice = JSON.parse(decodedJson) as {
                    title: string;
                    content: string;
                    color:
                        | 'success'
                        | 'warning'
                        | 'error'
                        | 'secondary'
                        | 'green'
                        | 'yellow'
                        | 'gray';
                };
                return (
                    <PostNotice
                        key={`${index}-postNotice`}
                        title={notice.title}
                        content={notice.content}
                        color={notice.color}
                    />
                );
            }

            // —————————————
            // 4) TGJU WRAPPER (ONLY ON CLIENT)
            // —————————————
            if (isClient && element.name === 'div' && element.attribs.class === 'tgju-wrapper') {
                const tgjuElement = element.children.find(
                    (child) => (child as Element).name === 'tgju',
                ) as Element | undefined;
                const scriptElement = element.children.find(
                    (child) => (child as Element).name === 'script',
                ) as Element | undefined;

                if (tgjuElement) {
                    const { type, items, columns, token } = tgjuElement.attribs;
                    return (
                        <div key={`${index}-tgju-wrapper`} className='tgju-wrapper'>
                            {/* Bypass JSX typing by creating the element manually */}
                            {React.createElement('tgju', {
                                type: type,
                                items: items,
                                columns: columns,
                                token: token,
                            })}

                            {scriptElement && (
                                <Script
                                    id='tgju'
                                    src={scriptElement.attribs.src}
                                    strategy='lazyOnload'
                                />
                            )}
                        </div>
                    );
                }
            }

            // —————————————
            // 5) CUSTOM IMAGE (data-custom)
            // —————————————
            if (element.name === 'img' && element.attribs['data-custom']) {
                const width = Number(element.attribs.width);
                const height = Number(element.attribs.height);

                if (isNaN(width) || isNaN(height)) {
                    console.warn(`Invalid width/height for <img> at index ${index}, skipping.`);
                    return null;
                }

                return (
                    <PostCustomImage
                        key={index}
                        src={element.attribs.src}
                        alt={element.attribs.alt ?? `${title}-${index}`}
                        width={width}
                        height={height}
                    />
                );
            }

            // —————————————
            // 6) CUSTOM TABLE ELEMENT
            // —————————————
            if (element.name === 'customtableelement') {
                const thead = JSON.parse(element.attribs['data-thead']);
                const tbody = JSON.parse(element.attribs['data-tbody']);
                return <CustomTable key={index} thead={thead} tbody={tbody} />;
            }

            // —————————————
            // 7) TRADINGVIEW WIDGET (static example)
            // —————————————
            if (element.attribs && element.attribs.class === 'tradingview-widget-container') {
                const symbol = element.attribs['data-symbol'];
                if (symbol) {
                    return <TradingViewWidget key={index} symbol={symbol} />;
                }
                return null; // Don't render anything if the symbol is missing
            }

            // —————————————
            // 8) VIDEO SCRIPTS
            // —————————————
            // Inside parseContent.tsx -> Section 8: VIDEO SCRIPTS

            if (element.attribs?.id === 'video-scripts') {
                try {
                    const vId = parseInt(element.attribs['data-id'], 10);
                    const rawJson = element.attribs['data-scripts'];

                    // Use decode to handle HTML entities
                    const decodedJson = decode(rawJson);

                    // EXTRA STEP: If the JSON still fails, it's often due to
                    // how the backend escaped the quotes inside the HTML strings.
                    const videoScripts: VideoScript[] = JSON.parse(decodedJson);
                    const videoScript = videoScripts[vId - 1];

                    if (videoScript) {
                        return (
                            <VideoPlayer
                                key={`video-${vId}-${index}`}
                                aparatHnembed={videoScript.aparat}
                                youtubeHnembed={videoScript.youtube}
                                classNameCustom='lg:!mt-0 aspect-video'
                            />
                        );
                    }
                } catch (error) {
                    console.error('JSON Parsing failed for video-scripts:', error);
                    return null; // Prevent the whole page from crashing
                }
            }
        }

        // By default, let html-react-parser render the node unchanged
        return undefined;
    };

    // If there's no content, return null (or an empty fragment).
    if (!processedContent) return null;

    // Parse and return the React tree.
    return (
        <>
            {parse(processedContent, {
                replace: (domNode: DOMNode, index: number) => transformNode(domNode, index),
            })}
        </>
    );
}
